import { DateTime } from '@ksyos/date-time';
import { TableBody, TableCell, TableCellSelectRow, TableRow } from '@ksyos/design-system';
import { Link } from '@ksyos/router';
import type { IProcessActivityWithChecked } from '@frontend/components/interfaces';
import { ManagerAuthorization, PayoutRecipient } from '@common/enums';
import { DisplayShortWithTooltip } from '@frontend/components/elements/DisplayShortWithTooltip';
import { FormatCurrency } from '@frontend/components/elements/FormatCurrency';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { isProcessingLabelVisible, isUsedForReprocessing } from '../PaymentsSearchFilters/paymentsHelperFunctions';

interface IPaymentsTableBodyProps {
    items: IProcessActivityWithChecked[];
    onSelectPaymentItem: (value: boolean, index: number) => void;
    reprocessedActivities: string[];
}

export function PaymentsTableBody(props: IPaymentsTableBodyProps) {
    const language = getSelectedLanguage();

    return (
        <TableBody>
            {props.items.map((item, i) => {
                const itemIsUsedForReprocessing = isUsedForReprocessing(item, props.items);
                let institutionToBePaid: string | null = null;

                if (item.payoutRecord.entityToPay === PayoutRecipient.organization) {
                    institutionToBePaid = item.payoutRecord.providerOrganization;
                } else if (item.payoutRecord.entityToPay === PayoutRecipient.group) {
                    institutionToBePaid = item.payoutRecord.providerGroup;
                }

                return (
                    <TableRow key={i}>
                        {itemIsUsedForReprocessing ? (
                            <TableCellSelectRow
                                isSelected={item.checked}
                                onSelect={async (event) =>
                                    props.onSelectPaymentItem((event.target as HTMLInputElement).checked, i)
                                }
                            />
                        ) : (
                            <TableCell />
                        )}
                        <TableCell>
                            <NullableFormattedDate value={item.dateSent} />
                        </TableCell>
                        <TableCell>{language.payoutType[item.activityType]}</TableCell>
                        <TableCell>
                            <DisplayShortWithTooltip
                                maxLength={10}
                                text={item.performerOrganization.name}
                            />
                        </TableCell>
                        <TableCell>
                            <DisplayShortWithTooltip
                                maxLength={20}
                                practitioner={item.performerPractitioner}
                            />
                        </TableCell>
                        <TableCell>{item.service.code}</TableCell>
                        <TableCell>
                            {hasOneAuthorization([
                                ManagerAuthorization.patientProcesses,
                                ManagerAuthorization.adminProcessRead,
                            ]) ? (
                                <Link
                                    to={
                                        '/processes/' +
                                        (item.processUuid ? 'new/' : '') +
                                        (item.requestId ?? item.processUuid)
                                    }
                                >
                                    {item.processId}
                                </Link>
                            ) : (
                                item.processId
                            )}
                        </TableCell>
                        <TableCell>
                            {item.noPayoutReason ? (
                                <DisplayShortWithTooltip
                                    maxLength={20}
                                    text={language.noPayoutReasonEnum[item.noPayoutReason]}
                                />
                            ) : (
                                ''
                            )}
                        </TableCell>
                        <TableCell
                            sx={{
                                borderLeft: '8px solid',
                                borderLeftColor: 'neutral100',
                            }}
                        >
                            <NullableFormattedDate value={item.payoutRecord.date} />
                        </TableCell>
                        <TableCell>
                            {institutionToBePaid ? (
                                <DisplayShortWithTooltip
                                    maxLength={20}
                                    text={institutionToBePaid}
                                />
                            ) : (
                                ''
                            )}
                        </TableCell>
                        <TableCell>
                            {item.payoutRecord.entityToPay === PayoutRecipient.practitioner ? (
                                <DisplayShortWithTooltip
                                    maxLength={20}
                                    practitioner={item.payoutRecord.practitioner}
                                />
                            ) : (
                                ''
                            )}
                        </TableCell>
                        <TableCell>{item.payoutRecord.relationNumber}</TableCell>
                        <TableCell>
                            {item.payoutRecord.providerProfession
                                ? language.professionEnum[item.payoutRecord.providerProfession]
                                : ''}
                        </TableCell>
                        <TableCell>
                            <FormatCurrency value={item.payoutRecord.amount || undefined} />
                        </TableCell>
                        <TableCell>{item.payoutRecord.runNumber}</TableCell>
                        <TableCell>
                            {itemIsUsedForReprocessing && item.reprocessPractitioner && (
                                <DisplayShortWithTooltip
                                    maxLength={20}
                                    practitioner={item.reprocessPractitioner}
                                />
                            )}
                        </TableCell>
                        <TableCell>
                            {itemIsUsedForReprocessing &&
                                item.reprocessDate &&
                                DateTime.from(item.reprocessDate).format('DD-MM-YYYY HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                            {isProcessingLabelVisible(item, props.items, props.reprocessedActivities)
                                ? language.finance.processing
                                : ''}
                        </TableCell>
                    </TableRow>
                );
            })}
        </TableBody>
    );
}
